import { api } from './api';

export interface ExportParams {
  from?: string;
  to?: string;
  status?: string;
}

/**
 * Fetch a CSV from an authed admin endpoint and trigger a browser download.
 * Uses the shared axios instance so the bearer token is attached automatically
 * (avoids putting the token in a URL).
 */
const downloadCsv = async (path: string, filename: string, params?: object) => {
  const res = await api.get(path, { params, responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const exportRides = (params?: ExportParams) => downloadCsv('/admin/export/rides', 'rides.csv', params);
export const exportPayments = (params?: ExportParams) => downloadCsv('/admin/export/payments', 'payments.csv', params);
export const exportWalletTransactions = (params?: ExportParams) =>
  downloadCsv('/admin/export/wallet-transactions', 'wallet-transactions.csv', params);
export const exportAuditLogs = (params?: ExportParams) =>
  downloadCsv('/admin/export/audit-logs', 'audit-logs.csv', params);

/**
 * Bank bulk-upload CSV formats for payout exports. Mirrors the backend's PAYOUT_CSV_BUILDERS
 * registry — the format is always explicit (no default: the operator must consciously pick which
 * bank's template they are uploading). Adding a provider = one backend builder + one entry here.
 */
export const PAYOUT_CSV_FORMATS = [
  { value: 'razorpayx', label: 'RazorpayX CSV' },
  { value: 'cashfree', label: 'Cashfree CSV' },
] as const;

export type PayoutCsvFormat = (typeof PAYOUT_CSV_FORMATS)[number]['value'];

export interface DriverUpiExportParams {
  /** Independent, combinable city filters (on the driver's current / home city). */
  currentCity?: string;
  homeCity?: string;
  /** Optional per-export override of the amount/unit (default comes from AppConfig settings). */
  amount?: number;
  amountUnit?: 'rupees' | 'paise';
  narration?: string;
  purpose?: string;
}

/** Full driver-UPI list. format is required: 'plain' coverage sheet, or a bank template. */
export const exportDriverUpi = (format: PayoutCsvFormat | 'plain', params?: DriverUpiExportParams) =>
  downloadCsv(
    '/admin/export/driver-upi',
    `driver-upi-${format}-${new Date().toISOString().slice(0, 10)}.csv`,
    { ...params, format },
  );

/** Bulk-payout CSV for one coin-backed batch (per-driver amounts = their coins), in the given bank's template. */
export const exportPayoutBatch = (
  id: number,
  format: PayoutCsvFormat,
  params?: { purpose?: string; narration?: string },
) =>
  downloadCsv(`/admin/payouts/batch/${id}/export`, `payout-batch-${id}-${format}.csv`, { ...params, format });
